import React, { Component } from "react"
import PropTypes from "prop-types"
import { Flex, Grid, Box } from "datastory-shared/ui"

import { isBrowser } from "datastory-shared/utils/browser"

import Chart from "./src/Chart"
import XDomainUI from "./src/XDomainUI"
import { getPartyTrends, getPollsData, getPollLines } from "./src/data-helpers"

class TrendLines extends Component {
  static propTypes = {
    polls: PropTypes.array,
    trends: PropTypes.array,
    trendsFormula: PropTypes.string,
    metaParties: PropTypes.array,
    metaPolls: PropTypes.array,
    selected: PropTypes.array,
    opacityNonSelected: PropTypes.number,
    visibilityNonSelected: PropTypes.oneOf(["faded", "hidden"]),
    location: PropTypes.object,
    shareUrl: PropTypes.string,
    trendFormulas: PropTypes.array,
    trendFormula: PropTypes.string,
    xDomain: PropTypes.string,
    xDomains: PropTypes.array,
    onXDomainChange: PropTypes.func,
    yDomain: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    yTickValues: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    showPolls: PropTypes.bool,
    showConfidenceIntervals: PropTypes.bool,
    noConfig: PropTypes.bool,
    annotations: PropTypes.array,
    drawPollLines: PropTypes.bool,
    showLineTooltip: PropTypes.bool,
    pointTooltipFormat: PropTypes.func,
    pointTooltipSticky: PropTypes.bool,
    pointTooltipOffset: PropTypes.array,
    pointOpacity: PropTypes.number,

    children: PropTypes.node,
    isButtonGroupAtTop: PropTypes.bool,
  }

  static defaultProps = {
    isButtonGroupAtTop: true,
    polls: [],
    trends: [],
    metaParties: [],
    metaPolls: [],
    selected: [],
    opacityNonSelected: 0.05,
    visibilityNonSelected: "faded",
    noConfig: false,
    trendFormulas: [
      {
        id: "mama",
        name: '"Mätningarnas Mätning"',
        desc: "Endast de 3 opinionsinstitut med högst kvalitet ingår.",
      },
    ],
    trendFormula: "mama",
    // xDomain: "since-last-election",
    xDomain: "6m",
    xDomains: [
      // {
      //   id: "since-last-election",
      //   label: "Sedan valet 2018",
      //   extent: ["", "2017-02-01"],
      // },
      { id: "12m", label: "12 mån", extent: ["1y", "2018-10-01"] },
      { id: "6m", label: "6 mån", extent: ["6m", "2018-10-01"] },
    ],
    showPolls: false,
    showConfidenceIntervals: false,
    drawPollLines: false,
    showLineTooltip: true,
    pointTooltipSticky: false,
    pointOpacity: 0.8,
    // try to get the location for the Share
    // if not passed from the parent component
    // if we're in the browser, get it from window
    location: isBrowser ? window.location : undefined,
  }

  constructor(props) {
    super(props)

    const partyIds = props.metaParties.map(p => p.id)
    this.state = {
      trends: getPartyTrends(
        props.trends,
        props.selected.length ? props.selected : partyIds
      ),
      polls: getPollsData(props.polls, partyIds),
      pollLines:
        props.drawPollLines &&
        getPollLines(
          props.polls,
          props.selected,
          props.metaPolls.map(p => p.id)
        ),
      metaParties: props.metaParties,
      metaPolls: props.metaPolls,
      xDomain: props.xDomain,
      yDomain: props.yDomain,
      showPolls: !!props.selected.length || props.showPolls,
      showConfidenceIntervals:
        !!props.selected.length || props.showConfidenceIntervals,
      selected: props.selected,
      opacityNonSelected: props.opacityNonSelected,
      visibilityNonSelected: props.visibilityNonSelected,
      noConfig: props.noConfig,
    }
  }

  static getDerivedStateFromProps(props) {
    const partyIds = props.metaParties.map(p => p.id)
    return {
      trends: getPartyTrends(
        props.trends,
        props.selected.length ? props.selected : partyIds
      ),
      polls: getPollsData(props.polls, partyIds),
      pollLines:
        props.drawPollLines &&
        getPollLines(
          props.polls,
          props.selected,
          props.metaPolls.map(p => p.id)
        ),
      selected: props.selected,
    }
  }

  handleSelectedChange = id => {
    // if the passed argument is an array, set the selected to it directly
    // allows for clearing all at once
    if (Array.isArray(id))
      return this.setState({
        selected: id,
        yDomain: id.length ? "selected" : undefined,
        showPolls: !!id.length,
        showConfidenceIntervals: !!id.length,
      })

    // eslint-disable-next-line
    const selected = [...this.state.selected]
    const index = selected.indexOf(id)

    if (index > -1) selected.splice(index, 1)
    else selected.push(id)

    this.setState({
      selected,
      yDomain: selected.length ? "selected" : undefined,
      showPolls: !!selected.length,
      showConfidenceIntervals: !!selected.length,
    })
  }

  handleXDomainChange = async xDomain => {
    const { onXDomainChange } = this.props
    this.setState({ xDomain })
    if (typeof onXDomainChange === "function") await onXDomainChange(xDomain)
  }

  render() {
    const {
      polls,
      trends,
      pollLines,
      metaParties,
      metaPolls,
      xDomain,
      yDomain,
      showPolls,
      showConfidenceIntervals,
      selected,
      opacityNonSelected,
      visibilityNonSelected,
      noConfig,
    } = this.state

    const {
      xDomains,
      annotations,
      showLineTooltip,
      pointTooltipFormat,
      pointTooltipSticky,
      pointTooltipOffset,
      pointOpacity,
      yTickValues,
      children,
      isButtonGroupAtTop,
    } = this.props

    return (
      <>
        <Grid gridRowGap="1.25rem" mb={["sm", "md"]}>
          {children}
          {isButtonGroupAtTop && (
            <Box gridColumn={["span 12", null, "span 7"]}>
              <Flex justifyContent="flex-end">
                <Box width={["100%", null, "auto"]}>
                  {!noConfig && (
                    <XDomainUI
                      xDomain={xDomain}
                      xDomains={xDomains}
                      onXDomainChange={this.handleXDomainChange}
                    />
                  )}
                </Box>
              </Flex>
            </Box>
          )}
        </Grid>
        <Box>
          <Chart
            polls={polls}
            trends={trends}
            pollLines={pollLines}
            metaParties={metaParties}
            metaPolls={metaPolls}
            xDomain={xDomain}
            xDomains={xDomains}
            yDomain={yDomain}
            yTickValues={yTickValues}
            showPolls={showPolls}
            showConfidenceIntervals={showConfidenceIntervals}
            opacityNonSelected={opacityNonSelected}
            visibilityNonSelected={visibilityNonSelected}
            selected={selected}
            onSelectedChange={this.handleSelectedChange}
            annotations={annotations}
            showLineTooltip={showLineTooltip}
            pointTooltipFormat={pointTooltipFormat}
            pointTooltipSticky={pointTooltipSticky}
            pointTooltipOffset={pointTooltipOffset}
            pointOpacity={pointOpacity}
          />
        </Box>
        {!isButtonGroupAtTop && !noConfig && (
          <XDomainUI
            xDomain={xDomain}
            xDomains={xDomains}
            onXDomainChange={this.handleXDomainChange}
          />
        )}
      </>
    )
  }
}

export default TrendLines
